import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const UserWAReports = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [activeReport, setActiveReport] = useState('delivery');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/reports?range=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data || {});
      }
    } catch (err) {
      console.error('Failed to fetch WA reports:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const reportTabs = [
    { key: 'delivery', label: 'Delivery Report', icon: 'CheckCheck' },
    { key: 'billing', label: 'Billing Report', icon: 'Receipt' },
    { key: 'quality', label: 'Quality & Health', icon: 'Shield' },
    { key: 'engagement', label: 'Engagement', icon: 'TrendingUp' },
  ];

  const renderEmpty = (icon, title, desc) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
        {getIcon(icon, 32)}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.BarChart3 className="w-5 h-5 text-white" />
            </div>
            WhatsApp Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">Analytics and reporting for your WhatsApp Business Account</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-gray-200 overflow-hidden">
            {['7d', '30d', '90d', 'custom'].map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className="px-3 py-2 text-xs font-medium transition-all"
                style={{ background: dateRange === range ? '#25D366' : 'white', color: dateRange === range ? 'white' : '#6b7280' }}
              >
                {range === 'custom' ? 'Custom' : range}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            <Icons.Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Sent', value: (stats.total_sent || 0).toLocaleString(), icon: 'Send', color: '#25D366', change: stats.sent_change },
          { label: 'Delivered', value: (stats.total_delivered || 0).toLocaleString(), icon: 'CheckCheck', color: '#128C7E', change: stats.delivered_change },
          { label: 'Read', value: (stats.total_read || 0).toLocaleString(), icon: 'Eye', color: '#34B7F1', change: stats.read_change },
          { label: 'Failed', value: (stats.total_failed || 0).toLocaleString(), icon: 'AlertTriangle', color: '#ef4444', change: stats.failed_change },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 20)}
              </div>
              {stat.change && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stat.change > 0 ? 'text-green-700 bg-green-50' : stat.change < 0 ? 'text-red-700 bg-red-50' : 'text-gray-700 bg-gray-50'}`}>
                  {stat.change > 0 ? '+' : ''}{stat.change}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Report Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {reportTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveReport(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2 whitespace-nowrap"
              style={{
                borderColor: activeReport === tab.key ? '#25D366' : 'transparent',
                color: activeReport === tab.key ? '#25D366' : '#6b7280',
                background: activeReport === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
              <p className="text-sm text-gray-500">Loading report data...</p>
            </div>
          ) : (
            <>
              {activeReport === 'delivery' && (
                <div>
                  {stats.total_sent > 0 ? (
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-xl p-5">
                        <h3 className="font-semibold text-gray-900 mb-4">Delivery Funnel</h3>
                        <div className="space-y-3">
                          {[
                            { label: 'Sent', value: stats.total_sent || 0, pct: 100, color: '#25D366' },
                            { label: 'Delivered', value: stats.total_delivered || 0, pct: stats.total_sent > 0 ? ((stats.total_delivered / stats.total_sent) * 100).toFixed(1) : 0, color: '#128C7E' },
                            { label: 'Read', value: stats.total_read || 0, pct: stats.total_sent > 0 ? ((stats.total_read / stats.total_sent) * 100).toFixed(1) : 0, color: '#34B7F1' },
                            { label: 'Replied', value: stats.total_replied || 0, pct: stats.total_sent > 0 ? ((stats.total_replied || 0) / stats.total_sent * 100).toFixed(1) : 0, color: '#075E54' },
                          ].map((step) => (
                            <div key={step.label} className="flex items-center gap-4">
                              <span className="text-sm font-medium text-gray-600 w-20">{step.label}</span>
                              <div className="flex-1 h-8 bg-white rounded-lg overflow-hidden border border-gray-100">
                                <div className="h-full rounded-lg flex items-center px-3 transition-all" style={{ width: `${Math.max(step.pct, 2)}%`, background: step.color }}>
                                  <span className="text-xs font-bold text-white whitespace-nowrap">{(step.value || 0).toLocaleString()} ({step.pct}%)</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : renderEmpty('CheckCheck', 'No Delivery Data', 'Start sending WhatsApp messages to see delivery analytics here.')}
                </div>
              )}

              {activeReport === 'billing' && (
                <div>
                  {stats.billing ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            { label: 'Total Conversations', value: (stats.billing.total_conversations || 0).toLocaleString(), color: '#25D366' },
                            { label: 'Estimated Cost', value: `₹${(stats.billing.total_cost || 0).toLocaleString()}`, color: '#075E54' },
                          ].map((s) => (
                            <div key={s.label} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                            </div>
                          ))}
                        </div>
                        <div className="bg-gray-50 rounded-xl p-5">
                          <h3 className="font-semibold text-gray-900 mb-3">By Category</h3>
                          {(stats.billing.categories || []).length > 0 ? (
                            <div className="space-y-2">
                              {stats.billing.categories.map((cat) => (
                                <div key={cat.name} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900 capitalize">{cat.name}</p>
                                    <p className="text-xs text-gray-400">{(cat.count || 0).toLocaleString()} conversations</p>
                                  </div>
                                  <p className="text-sm font-bold text-gray-900">₹{(cat.cost || 0).toLocaleString()}</p>
                                </div>
                              ))}
                            </div>
                          ) : <p className="text-sm text-gray-500 text-center py-4">No category breakdown available.</p>}
                        </div>
                      </div>
                    </div>
                  ) : renderEmpty('Receipt', 'No Billing Data', 'Billing data will appear once conversations are initiated through your WABA.')}
                </div>
              )}

              {activeReport === 'quality' && (
                <div>
                  {stats.quality ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { label: 'Quality Rating', value: stats.quality.rating || 'N/A', icon: 'Shield', color: stats.quality.rating === 'GREEN' ? '#22c55e' : stats.quality.rating === 'YELLOW' ? '#f59e0b' : stats.quality.rating === 'RED' ? '#ef4444' : '#9ca3af' },
                        { label: 'Phone Status', value: stats.quality.phone_status || 'N/A', icon: 'Phone', color: '#128C7E' },
                        { label: 'Message Limit', value: stats.quality.messaging_limit || 'N/A', icon: 'Gauge', color: '#34B7F1' },
                        { label: 'Template Approval', value: stats.quality.template_approval_rate || 'N/A', icon: 'FileCheck', color: '#075E54' },
                      ].map((stat) => (
                        <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${stat.color}14`, color: stat.color }}>
                            {getIcon(stat.icon, 20)}
                          </div>
                          <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  ) : renderEmpty('Shield', 'No Quality Data', 'Quality metrics will appear once your WABA account is active and sending messages.')}
                </div>
              )}

              {activeReport === 'engagement' && (
                <div>
                  {stats.engagement ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { label: 'Click-through Rate', value: stats.engagement.ctr || '0%', icon: 'MousePointerClick', color: '#25D366' },
                        { label: 'Reply Rate', value: stats.engagement.reply_rate || '0%', icon: 'Reply', color: '#128C7E' },
                        { label: 'Opt-out Rate', value: stats.engagement.optout_rate || '0%', icon: 'UserMinus', color: '#ef4444' },
                        { label: 'Avg Session', value: stats.engagement.avg_session || 'N/A', icon: 'Timer', color: '#34B7F1' },
                      ].map((stat) => (
                        <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${stat.color}14`, color: stat.color }}>
                            {getIcon(stat.icon, 20)}
                          </div>
                          <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  ) : renderEmpty('TrendingUp', 'No Engagement Data', 'Engagement analytics will populate as WhatsApp interactions occur.')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserWAReports;
